import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/axios';
import { useToast } from '../context/ToastContext';

const STATUSES = [
  { value: 'present', label: 'Present', color: '#10b981', bg: 'rgba(16,185,129,0.15)' },
  { value: 'absent', label: 'Absent', color: '#ef4444', bg: 'rgba(239,68,68,0.15)' },
  { value: 'half_day', label: 'Half Day', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  { value: 'late', label: 'Late', color: '#f97316', bg: 'rgba(249,115,22,0.15)' },
  { value: 'leave', label: 'Leave', color: '#8b5cf6', bg: 'rgba(139,92,246,0.15)' },
];

const StatusBadge = ({ status }) => {
  const s = STATUSES.find(st => st.value === status) || STATUSES[0];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
      textTransform: 'uppercase', letterSpacing: 0.5,
      background: s.bg, color: s.color, border: `1px solid ${s.color}30`
    }}>
      {s.label}
    </span>
  );
};

const AttendancePage = () => {
  const [tab, setTab] = useState('today');
  const [todayData, setTodayData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [histMonth, setHistMonth] = useState(new Date().getMonth() + 1);
  const [histYear, setHistYear] = useState(new Date().getFullYear());
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const fetchToday = useCallback(async () => {
    try {
      const { data } = await api.get('/attendance/today');
      setTodayData(data.data);
    } catch { toast('Failed to load today attendance', 'error'); }
    finally { setLoading(false); }
  }, [toast]);

  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await api.get(`/attendance/summary?month=${month}&year=${year}`);
      setSummaryData(data.data);
    } catch { toast('Failed to load summary', 'error'); }
    finally { setLoading(false); }
  }, [month, year, toast]);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/attendance?month=${histMonth}&year=${histYear}`);
      setHistoryData(data.data);
    } catch { toast('Failed to load history', 'error'); }
    finally { setLoading(false); }
  }, [histMonth, histYear, toast]);

  useEffect(() => {
    setLoading(true);
    if (tab === 'today') fetchToday();
    else if (tab === 'summary') fetchSummary();
    else fetchHistory();
  }, [tab, fetchToday, fetchSummary, fetchHistory]);

  const handleClockIn = async (staffId) => {
    setSaving(true);
    try {
      await api.post('/attendance/clock-in', { staffId });
      toast('Clocked in!', 'success');
      fetchToday();
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to clock in', 'error');
    } finally { setSaving(false); }
  };

  const handleClockOut = async (staffId) => {
    setSaving(true);
    try {
      await api.post('/attendance/clock-out', { staffId });
      toast('Clocked out!', 'success');
      fetchToday();
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to clock out', 'error');
    } finally { setSaving(false); }
  };

  const handleMarkAttendance = async (staffId, status) => {
    setSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      await api.post('/attendance/mark', { staffId, date: today, status });
      toast(`Marked ${status}`, 'success');
      fetchToday();
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to mark', 'error');
    } finally { setSaving(false); }
  };

  const formatTime = (ts) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  if (loading) return <div className="flex-center" style={{ height: 300 }}><div className="spinner"></div></div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700 }}>🕐 Employee Attendance</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>Track daily attendance, clock in/out, and monthly summaries</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'var(--dark-2)', borderRadius: 'var(--radius-sm)', padding: 4, border: '1px solid var(--border)', width: 'fit-content' }}>
        {[{ key: 'today', label: "📋 Today's Attendance" }, { key: 'summary', label: '📊 Monthly Summary' }, { key: 'history', label: '📅 History' }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              padding: '8px 18px', borderRadius: 6, border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              background: tab === t.key ? 'linear-gradient(135deg, var(--primary), var(--primary-dark))' : 'transparent',
              color: tab === t.key ? '#fff' : 'var(--text-muted)',
              transition: 'all 0.2s'
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* TODAY TAB */}
      {tab === 'today' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">📋 Today — {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{todayData.filter(s => s.status).length}/{todayData.length} marked</span>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Role</th>
                  <th>Clock In</th>
                  <th>Clock Out</th>
                  <th>Hours</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {todayData.map(s => (
                  <tr key={s.staff_id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, var(--primary), var(--secondary))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, color: '#fff' }}>
                          {(s.full_name || s.username)[0].toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13.5 }}>{s.full_name || s.username}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.phone || '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td><span className={`badge badge-${s.role}`}>{s.role}</span></td>
                    <td style={{ fontWeight: 600, color: s.clock_in ? 'var(--success)' : 'var(--text-muted)' }}>{formatTime(s.clock_in)}</td>
                    <td style={{ fontWeight: 600, color: s.clock_out ? 'var(--danger)' : 'var(--text-muted)' }}>{formatTime(s.clock_out)}</td>
                    <td style={{ fontWeight: 600 }}>{s.hours_worked ? `${parseFloat(s.hours_worked).toFixed(1)}h` : '—'}</td>
                    <td>{s.status ? <StatusBadge status={s.status} /> : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Not marked</span>}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {!s.clock_in && (
                          <button className="btn btn-success btn-sm" onClick={() => handleClockIn(s.staff_id)} disabled={saving}>🕐 In</button>
                        )}
                        {s.clock_in && !s.clock_out && (
                          <button className="btn btn-danger btn-sm" onClick={() => handleClockOut(s.staff_id)} disabled={saving}>🕐 Out</button>
                        )}
                        <select
                          style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--dark-3)', color: 'var(--text)', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}
                          value={s.status || ''}
                          onChange={e => handleMarkAttendance(s.staff_id, e.target.value)}
                          disabled={saving}
                        >
                          <option value="" disabled>Mark...</option>
                          {STATUSES.map(st => <option key={st.value} value={st.value}>{st.label}</option>)}
                        </select>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SUMMARY TAB */}
      {tab === 'summary' && (
        <div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center' }}>
            <select className="form-select" style={{ width: 160 }} value={month} onChange={e => setMonth(parseInt(e.target.value))}>
              {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            <select className="form-select" style={{ width: 100 }} value={year} onChange={e => setYear(parseInt(e.target.value))}>
              {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">📊 Attendance Summary — {monthNames[month - 1]} {year}</span>
            </div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th style={{ textAlign: 'center' }}>✅ Present</th>
                    <th style={{ textAlign: 'center' }}>❌ Absent</th>
                    <th style={{ textAlign: 'center' }}>½ Half Day</th>
                    <th style={{ textAlign: 'center' }}>⏰ Late</th>
                    <th style={{ textAlign: 'center' }}>🏖 Leave</th>
                    <th style={{ textAlign: 'center' }}>Avg Hours</th>
                    <th style={{ textAlign: 'center' }}>Total Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryData.map(s => (
                    <tr key={s.staff_id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{s.full_name || s.username}</div>
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: 'var(--success)' }}>{s.present_days}</td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: 'var(--danger)' }}>{s.absent_days}</td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: 'var(--warning)' }}>{s.half_days}</td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: '#f97316' }}>{s.late_days}</td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: '#8b5cf6' }}>{s.leave_days}</td>
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>{s.avg_hours}h</td>
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>{parseFloat(s.total_hours).toFixed(1)}h</td>
                    </tr>
                  ))}
                  {summaryData.length === 0 && (
                    <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No data for this month</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* HISTORY TAB */}
      {tab === 'history' && (
        <div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center' }}>
            <select className="form-select" style={{ width: 160 }} value={histMonth} onChange={e => setHistMonth(parseInt(e.target.value))}>
              {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            <select className="form-select" style={{ width: 100 }} value={histYear} onChange={e => setHistYear(parseInt(e.target.value))}>
              {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">📅 Attendance Records — {monthNames[histMonth - 1]} {histYear}</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{historyData.length} records</span>
            </div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Employee</th>
                    <th>Clock In</th>
                    <th>Clock Out</th>
                    <th>Hours</th>
                    <th>Status</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {historyData.map(a => (
                    <tr key={a.id}>
                      <td style={{ fontWeight: 600, fontSize: 12 }}>
                        {new Date(a.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{a.full_name || a.username}</div>
                      </td>
                      <td style={{ fontSize: 12, color: a.clock_in ? 'var(--success)' : 'var(--text-muted)' }}>{formatTime(a.clock_in)}</td>
                      <td style={{ fontSize: 12, color: a.clock_out ? 'var(--danger)' : 'var(--text-muted)' }}>{formatTime(a.clock_out)}</td>
                      <td style={{ fontWeight: 600 }}>{a.hours_worked ? `${parseFloat(a.hours_worked).toFixed(1)}h` : '—'}</td>
                      <td><StatusBadge status={a.status} /></td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.notes || '—'}</td>
                    </tr>
                  ))}
                  {historyData.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No records for this month</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendancePage;
